import { FAQ_ITEMS, BUSINESS } from "#/lib/constants";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { CTAButton } from "#/components/shared/CTAButton";
import { useScrollAnimation } from "#/hooks/useScrollAnimation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "#/components/ui/accordion";
import { Phone } from "lucide-react";

export function HomeFAQ() {
  const { ref, isVisible } = useScrollAnimation(0.05);

  return (
    <section id="faq" className="bg-light-bg py-20 lg:py-28">
      {/* Explicit grid placement so the DOM order can be heading, then the
          questions, then the "still have a question" card. On mobile that
          stacks in reading order (the card used to sit above the FAQs it is
          meant to follow); on desktop the card returns to the left column
          under the heading. */}
      <div ref={ref} className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.8fr_1.2fr] lg:gap-x-16 lg:gap-y-8 lg:px-8">
        {/* Heading */}
        <div className="lg:col-start-1 lg:row-start-1">
          <SectionLabel>Questions, Answered</SectionLabel>
          <h2
            className={`mt-4 font-heading text-3xl font-extrabold leading-[1.1] text-graphite transition-all duration-700 sm:text-4xl ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            Straight answers about concrete in Delaware.
          </h2>
          <p className="mt-5 max-w-sm leading-relaxed text-concrete-dark">
            Cost, cure time, stamped vs. pavers, permits: here's what homeowners ask us most.
            Still unsure? We're happy to talk it through.
          </p>
        </div>

        {/* Accordion — right column on desktop, spanning both rows */}
        <div
          className={`transition-all delay-100 duration-700 lg:col-start-2 lg:row-start-1 lg:row-span-2 ${
            isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
          }`}
        >
          <Accordion type="single" collapsible className="space-y-3" defaultValue="item-0">
            {FAQ_ITEMS.map((faq, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white px-5 last:border-b"
              >
                <AccordionTrigger className="py-5 text-left font-heading text-base font-bold text-graphite hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-concrete-dark">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* "Still have a question" — after the questions on mobile, back under
            the heading on desktop. The two actions sit on one row at every
            width: they used to wrap into a stack on a phone. */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 lg:col-start-1 lg:row-start-2 lg:self-start">
          <p className="font-heading text-lg font-bold text-graphite">Still have a question?</p>
          <p className="mt-1 text-sm text-concrete-dark">
            Call and talk to someone who actually pours concrete.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <CTAButton href="#contact" variant="flame-bubble" className="w-full justify-center">
              Free Estimate
            </CTAButton>
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex min-h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 px-2 text-sm font-semibold text-graphite no-underline transition-colors hover:border-flame hover:text-flame"
            >
              <Phone className="h-4 w-4 shrink-0 text-flame" />
              {BUSINESS.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
